/**
 * BaZi (八字 / Four Pillars of Destiny) engine — the four pillars.
 *
 * Self-contained: its only dependency is astronomy-engine (MIT, pure JS, no
 * data files). Nothing here is shared with any other project.
 *
 * Astronomically exact where it matters: the year boundary is Lichun (Sun at
 * ecliptic longitude 315°) and month boundaries are the twelve jie (every 30°
 * of solar longitude from 315°), both taken from the real Sun rather than
 * lookup tables. Day pillar comes from the Julian Day Number with the verified
 * anchor 2019-01-27 = jiǎzǐ (甲子) day.
 *
 * Conventions (documented in ../docs/02-calculation.md):
 *  - The civil day changes at local midnight (the common modern school); the
 *    23:00-starts-a-new-day school can be added as an option later.
 *  - Hour branch uses local civil time, not true solar time.
 */
import * as Astronomy from "astronomy-engine";

/** Wrap any angle into [0, 360). */
export function normalizeDeg(deg) {
  return ((deg % 360) + 360) % 360;
}

/** Sun's geocentric apparent ecliptic longitude of date, in degrees. */
export function sunLongitude(date) {
  return normalizeDeg(Astronomy.SunPosition(date).elon);
}

export const STEMS = [
    { chinese: "甲", pinyin: "jiǎ", en: "Yang Wood", th: "ไม้หยาง" },
    { chinese: "乙", pinyin: "yǐ", en: "Yin Wood", th: "ไม้หยิน" },
    { chinese: "丙", pinyin: "bǐng", en: "Yang Fire", th: "ไฟหยาง" },
    { chinese: "丁", pinyin: "dīng", en: "Yin Fire", th: "ไฟหยิน" },
    { chinese: "戊", pinyin: "wù", en: "Yang Earth", th: "ดินหยาง" },
    { chinese: "己", pinyin: "jǐ", en: "Yin Earth", th: "ดินหยิน" },
    { chinese: "庚", pinyin: "gēng", en: "Yang Metal", th: "ทองหยาง" },
    { chinese: "辛", pinyin: "xīn", en: "Yin Metal", th: "ทองหยิน" },
    { chinese: "壬", pinyin: "rén", en: "Yang Water", th: "น้ำหยาง" },
    { chinese: "癸", pinyin: "guǐ", en: "Yin Water", th: "น้ำหยิน" },
];

export const BRANCHES = [
    { chinese: "子", pinyin: "zǐ", animalEn: "Rat", animalTh: "ชวด" },
    { chinese: "丑", pinyin: "chǒu", animalEn: "Ox", animalTh: "ฉลู" },
    { chinese: "寅", pinyin: "yín", animalEn: "Tiger", animalTh: "ขาล" },
    { chinese: "卯", pinyin: "mǎo", animalEn: "Rabbit", animalTh: "เถาะ" },
    { chinese: "辰", pinyin: "chén", animalEn: "Dragon", animalTh: "มะโรง" },
    { chinese: "巳", pinyin: "sì", animalEn: "Snake", animalTh: "มะเส็ง" },
    { chinese: "午", pinyin: "wǔ", animalEn: "Horse", animalTh: "มะเมีย" },
    { chinese: "未", pinyin: "wèi", animalEn: "Goat", animalTh: "มะแม" },
    { chinese: "申", pinyin: "shēn", animalEn: "Monkey", animalTh: "วอก" },
    { chinese: "酉", pinyin: "yǒu", animalEn: "Rooster", animalTh: "ระกา" },
    { chinese: "戌", pinyin: "xū", animalEn: "Dog", animalTh: "จอ" },
    { chinese: "亥", pinyin: "hài", animalEn: "Pig", animalTh: "กุน" },
];

export const ELEMENTS = [
    { en: "Wood", th: "ไม้", chinese: "木" },
    { en: "Fire", th: "ไฟ", chinese: "火" },
    { en: "Earth", th: "ดิน", chinese: "土" },
    { en: "Metal", th: "ทอง", chinese: "金" },
    { en: "Water", th: "น้ำ", chinese: "水" },
];

/** Primary element of each branch: 子water 丑earth 寅wood 卯wood 辰earth 巳fire 午fire 未earth 申metal 酉metal 戌earth 亥water */
const BRANCH_ELEMENT = [4, 2, 0, 0, 2, 1, 1, 2, 3, 3, 2, 4];

export function stemElement(stem) {
    return Math.floor(stem / 2);
}
export function stemIsYang(stem) {
    return stem % 2 === 0;
}
export function branchElement(branch) {
    return BRANCH_ELEMENT[branch];
}

/** Indexed by relation d = (elem - dayMasterElem) mod 5, then [samePolarity ? 0 : 1]. */
export const TEN_GODS = [
    [
        { chinese: "比肩", pinyin: "bǐ jiān", en: "Friend", th: "มิตร" },
        { chinese: "劫財", pinyin: "jié cái", en: "Rob Wealth", th: "ชิงทรัพย์" },
    ],
    [
        { chinese: "食神", pinyin: "shí shén", en: "Eating God", th: "เทพโภชนา" },
        { chinese: "傷官", pinyin: "shāng guān", en: "Hurting Officer", th: "ขุนนางบาดเจ็บ" },
    ],
    [
        { chinese: "偏財", pinyin: "piān cái", en: "Indirect Wealth", th: "ทรัพย์เอียง" },
        { chinese: "正財", pinyin: "zhèng cái", en: "Direct Wealth", th: "ทรัพย์ตรง" },
    ],
    [
        { chinese: "七殺", pinyin: "qī shā", en: "Seven Killings", th: "เจ็ดสังหาร" },
        { chinese: "正官", pinyin: "zhèng guān", en: "Direct Officer", th: "ขุนนางตรง" },
    ],
    [
        { chinese: "偏印", pinyin: "piān yìn", en: "Indirect Resource", th: "ตราเอียง" },
        { chinese: "正印", pinyin: "zhèng yìn", en: "Direct Resource", th: "ตราตรง" },
    ],
];

/** The Ten-God relation of any stem relative to the Day Master stem. */
export function tenGodOfStem(dayMaster, stem) {
    const d = (stemElement(stem) - stemElement(dayMaster) + 5) % 5;
    const samePolarity = stemIsYang(stem) === stemIsYang(dayMaster);
    return TEN_GODS[d][samePolarity ? 0 : 1];
}

/** Julian Day Number of a Gregorian calendar date (value at local noon). */
export function jdnFromGregorian(y, m, d) {
    // Fliegel–Van Flandern: this division truncates toward zero (not floor) —
    // (m-14)/12 is negative for Jan-Dec inputs.
    const a = Math.trunc((m - 14) / 12);
    return (Math.floor((1461 * (y + 4800 + a)) / 4) +
        Math.floor((367 * (m - 2 - 12 * a)) / 12) -
        Math.floor((3 * Math.floor((y + 4900 + a) / 100)) / 4) +
        d -
        32075);
}

/** Sexagenary index 0..59 (0 = 甲子) of the civil day with the given JDN. Anchor: JDN 2458511 (2019-01-27) = 甲子. */
export function sexagenaryDay(jdn) {
    return (((jdn - 11) % 60) + 60) % 60;
}

export function pillarFromSexagenary(n) {
    return { stem: n % 10, branch: n % 12 };
}

/**
 * Compute the four pillars for a birth instant.
 * @param birthUTC   the birth instant (UTC)
 * @param tzOffsetMinutes  local civil offset from UTC in minutes (e.g. Bangkok +420)
 */
export function computeBazi(birthUTC, tzOffsetMinutes) {
    const local = new Date(birthUTC.getTime() + tzOffsetMinutes * 60000);
    const ly = local.getUTCFullYear();
    const lm = local.getUTCMonth() + 1;
    const ld = local.getUTCDate();
    const lh = local.getUTCHours();

    const lambda = sunLongitude(birthUTC);

    // Year pillar — the BaZi year begins at Lichun (Sun at 315°). Before Lichun,
    // Jan/early-Feb dates (Sun in the zǐ/chǒu months, λ ∈ [255°, 315°)) belong to
    // the previous year.
    let baziYear = ly;
    if (lm <= 2 && lambda >= 255 && lambda < 315) baziYear -= 1;
    const yearStem = (((baziYear - 4) % 10) + 10) % 10;
    const yearBranch = (((baziYear - 4) % 12) + 12) % 12;

    // Month pillar — ordinal 0 (寅 month) starts at λ=315°, one month per 30°.
    // Stem follows the Five Tigers rule (五虎遁) from the year stem.
    const monthOrdinal = Math.floor(normalizeDeg(lambda - 315) / 30);
    const monthBranch = (monthOrdinal + 2) % 12;
    const monthStem = ((yearStem % 5) * 2 + 2 + monthOrdinal) % 10;

    // Day pillar — from the JDN of the local civil date (day changes at local midnight).
    const day = pillarFromSexagenary(sexagenaryDay(jdnFromGregorian(ly, lm, ld)));

    // Hour pillar — 子 hour spans 23:00–00:59; stem via the Five Rats rule (五鼠遁).
    const hourBranch = Math.floor(((lh + 1) % 24) / 2);
    const hourStem = ((day.stem % 5) * 2 + hourBranch) % 10;

    const pillars = [
        { stem: yearStem, branch: yearBranch },
        { stem: monthStem, branch: monthBranch },
        day,
        { stem: hourStem, branch: hourBranch },
    ];

    const elementCounts = [0, 0, 0, 0, 0];
    for (const p of pillars) {
        elementCounts[stemElement(p.stem)]++;
        elementCounts[branchElement(p.branch)]++;
    }

    return {
        year: pillars[0],
        month: pillars[1],
        day,
        hour: pillars[3],
        dayMaster: day.stem,
        elementCounts,
        /** Solar longitude at birth — exposed so callers can locate the solar month. */
        sunLongitude: lambda,
        monthOrdinal,
    };
}

/** e.g. "甲子" — handy for tests and display. */
export function pillarName(p) {
    return STEMS[p.stem].chinese + BRANCHES[p.branch].chinese;
}
